export default function HandlingLeaseExpirations() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/8e2483ba341b1c475ace06a2156b3bac.jpg"
                alt="Managing lease expirations and renewals during apartment building sale"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border border-slate-100 max-w-xs">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <i className="ri-calendar-check-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">6-12</div>
                  <div className="text-sm text-slate-600">Months Ideal</div>
                </div>
              </div>
              <p className="text-xs text-slate-600">
                Optimal remaining lease term for maximum property value
              </p>
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-calendar-event-line"></i>
              <span>Lease Timing Strategy</span>
            </div>
            
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Handling Lease Expirations During the Sale Process
            </h2>
            
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              Lease expiration timing can significantly impact your property's value and marketability. Strategic management of lease renewals and expirations is crucial when planning to sell your apartment building.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">The Lease Expiration Challenge</h3>

            <p className="text-slate-700 mb-6 leading-relaxed">
              Properties with multiple leases expiring within 3-6 months of closing are less attractive to buyers because they face immediate tenant turnover risk. Conversely, leases with too much remaining term (3+ years) may lock buyers into unfavorable rates or terms. The sweet spot is typically 6-12 months of remaining lease term across your tenant base.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-6">
              <div className="flex items-start gap-3">
                <i className="ri-alert-line text-amber-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-amber-900 mb-2">Critical Timing Consideration</h4>
                  <p className="text-amber-800 text-sm leading-relaxed">
                    If more than 30% of your leases expire within 90 days of your planned closing date, expect buyers to request price reductions of 5-15% to account for turnover risk and potential vacancy losses.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">Strategic Renewal Approaches</h3>

            <div className="space-y-4">
              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">6-12 Months Before Sale</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Begin renewing leases that will expire during your anticipated sale period. Offer 12-month renewals at current market rates to demonstrate stable occupancy and income to potential buyers.
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-slate-600">
                        <strong>Example:</strong> If planning to sell in June, renew any leases expiring between April and September with 12-month terms starting from their expiration date.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-edit-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Short-Term Renewals for Problem Tenants</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      For tenants with payment issues or lease violations, consider 3-6 month renewals or month-to-month agreements. This gives the buyer flexibility to address problem tenancies without being locked into long-term leases.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Market-Rate Adjustments</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Renew at current market rates, not below-market "loyalty discounts." Buyers value properties at market rent levels, so below-market renewals actually reduce your property value.
                    </p>
                    <div className="flex items-center gap-4 text-sm mt-2">
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-arrow-up-line text-green-600"></i>
                        <span>Market rate = Higher value</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-arrow-down-line text-red-600"></i>
                        <span>Below market = Lower value</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Avoid Long-Term Commitments</h4>
                    <p className="text-slate-600 text-sm">
                      Don't offer 2-3 year leases within 6 months of listing. Buyers want flexibility to implement their own management strategies and may view long-term leases as constraints rather than benefits.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">What to Do When Leases Expire During Due Diligence</h3>
          
          <p className="text-slate-700 mb-6 leading-relaxed">
            If leases expire after you've accepted an offer but before closing, you have several options to maintain deal momentum and property value:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <span className="text-teal-600 font-bold">1</span>
                </div>
                <h4 className="font-semibold text-slate-900">Buyer-Approved Renewals</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Negotiate with the buyer to approve renewal terms before offering them to tenants. This ensures the buyer is comfortable with the lease terms they'll inherit.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Contract Language:</strong> "Seller may renew expiring leases with Buyer's prior written approval, which shall not be unreasonably withheld."
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <span className="text-teal-600 font-bold">2</span>
                </div>
                <h4 className="font-semibold text-slate-900">Month-to-Month Conversion</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Allow leases to convert to month-to-month tenancies until closing. This gives the buyer maximum flexibility while maintaining occupancy and income.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Note:</strong> Some buyers prefer this approach as it allows them to adjust rents or terms immediately after closing.
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <span className="text-teal-600 font-bold">3</span>
                </div>
                <h4 className="font-semibold text-slate-900">Buyer Direct Negotiation</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Introduce the buyer to tenants with expiring leases and allow them to negotiate renewal terms directly. This builds buyer-tenant relationships early.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Benefit:</strong> Buyer feels more in control and tenants begin relationship with new owner before closing.
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <span className="text-teal-600 font-bold">4</span>
                </div>
                <h4 className="font-semibold text-slate-900">Price Adjustment Agreement</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Negotiate a price reduction or credit for any units that become vacant before closing. Typically $2,000-$5,000 per vacant unit to cover turnover costs.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Formula:</strong> Credit = (Monthly rent × 2 months) + estimated turnover costs
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Lease Expiration Timeline Impact on Value</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Remaining Lease Term</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Buyer Perception</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Value Impact</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Recommendation</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">&lt; 3 months</td>
                  <td className="py-4 px-4 text-slate-600">High turnover risk</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                      <i className="ri-arrow-down-line"></i>
                      -10% to -15%
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">Renew immediately or convert to month-to-month</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">3-6 months</td>
                  <td className="py-4 px-4 text-slate-600">Moderate concern</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                      <i className="ri-arrow-down-line"></i>
                      -5% to -10%
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">Consider renewal with buyer approval</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">6-12 months</td>
                  <td className="py-4 px-4 text-slate-600">Ideal stability</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                      <i className="ri-checkbox-circle-line"></i>
                      Optimal
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">Perfect timing - no action needed</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">12-24 months</td>
                  <td className="py-4 px-4 text-slate-600">Good stability</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-teal-600 font-semibold">
                      <i className="ri-checkbox-line"></i>
                      Neutral
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">Acceptable - verify market rate rents</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-700">&gt; 24 months</td>
                  <td className="py-4 px-4 text-slate-600">Limited flexibility</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                      <i className="ri-alert-line"></i>
                      -3% to -8%
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">May deter buyers seeking operational control</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-slate-600 mt-6 italic">
            * Value impacts are approximate and vary based on market conditions, property type, and buyer preferences
          </p>
        </div>
      </div>
    </section>
  );
}
